import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { adminConfigApi, cycleApi, inventoryApi } from '../services/api'

/**
 * Dashboard — landing page after login.
 *
 * Sections (in order):
 *   1. Welcome banner — area name, logo, welcome message, dates/times
 *   2. Featured Items banner — up to 3 admin-curated items, each links
 *      to the matching card on the Shop page (deep-link via ?focus=id)
 *   3. Quick actions — Shop / Cart / My Orders (for users with a customer_id)
 *   4. Reports tile (for GSRs / Area Officers)
 *   5. Admin tiles (for sysadmins)
 *   6. Your access — friendly summary of what the user can do
 *
 * "Your access" wording adapts to customer kind:
 *   - kind='individual' → "Personal literature account"
 *   - otherwise        → "Standard literature rep for {customer_name}"
 */
const FEATURED_LIMIT_ON_DASHBOARD = 3

export default function Dashboard() {
    const { user, isSysAdmin } = useAuth()
    const [publicConfig, setPublicConfig] = useState(null)
    const [cycle, setCycle] = useState(null)
    const [featuredItems, setFeaturedItems] = useState([])

    useEffect(() => {
        loadPublicConfig()
        loadCycle()
        loadFeatured()
    }, [])

    async function loadPublicConfig() {
        try {
            const response = await adminConfigApi.getPublic()
            if (response.success) setPublicConfig(response.config)
        } catch {
            // Non-fatal
        }
    }

    async function loadCycle() {
        try {
            const response = await cycleApi.current()
            if (response?.label) setCycle(response)
        } catch {
            // Non-fatal
        }
    }

    async function loadFeatured() {
        try {
            const response = await inventoryApi.list()
            if (response.success) {
                const featured = (response.items || [])
                    .filter(i => i.isFeatured)
                    .slice(0, FEATURED_LIMIT_ON_DASHBOARD)
                setFeaturedItems(featured)
            }
        } catch {
            // Non-fatal
        }
    }

    const capabilities = [
        user?.isSysAdmin && 'Literature Chair',
        user?.isAreaAdmin && `Area Officer${user.areaPosition ? ` (${user.areaPosition})` : ''}`,
        user?.isGsr && 'GSR',
        user?.canUpload && 'Inventory Upload',
    ].filter(Boolean)

    const hasWelcomeContent = publicConfig?.welcomeMessage || publicConfig?.datesTimes
    const hasCustomer = !!user?.customerId
    const isPersonal = user?.customerKind === 'individual'

    return (
        <div className="space-y-6">
            {/* ----- Welcome banner --------------------------------------------- */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6">
                    <div className="flex items-start gap-4">
                        {publicConfig?.logoUrl && (
                            <img
                                src={publicConfig.logoUrl}
                                alt={publicConfig.areaName || 'Area logo'}
                                className="h-16 w-16 object-contain flex-shrink-0"
                            />
                        )}
                        <div className="flex-1 min-w-0">
                            <h2 className="text-2xl font-semibold text-ghasc-navy">
                                Welcome, {user?.name?.split(' ')[0] || 'friend'}.
                            </h2>
                            <p className="text-gray-600 mt-1">
                                {isPersonal
                                    ? 'Logged in to your personal account.'
                                    : user?.customerName
                                        ? `Logged in for ${user.customerName}.`
                                        : 'Logged in.'}
                            </p>
                        </div>
                    </div>

                    {hasWelcomeContent && (
                        <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                            {publicConfig.welcomeMessage && (
                                <p className="text-gray-700 whitespace-pre-line">
                                    {publicConfig.welcomeMessage}
                                </p>
                            )}
                            {publicConfig.datesTimes && (
                                <p className="text-sm text-gray-600 whitespace-pre-line">
                                    📅 {publicConfig.datesTimes}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {(cycle || publicConfig?.pickupMessageOverride) && (
                    <div className="bg-blue-50 border-t border-blue-200 px-6 py-3 text-sm text-blue-900">
                        <PickupDeadlineBanner
                            cycle={cycle}
                            override={publicConfig?.pickupMessageOverride}
                            overrideUntil={publicConfig?.pickupMessageOverrideUntil}
                        />
                    </div>
                )}
            </div>

            {/* ----- Featured Items banner -------------------------------------- */}
            {hasCustomer && featuredItems.length > 0 && (
                <FeaturedBanner items={featuredItems} />
            )}

            {/* ----- Quick actions for shopping users --------------------------- */}
            {hasCustomer && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-ghasc-navy mb-3">Place an order</h3>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <ActionLink to="/shop" title="Shop"
                            subtitle="Browse the catalog and add items to your cart" />
                        <ActionLink to="/cart" title="Cart"
                            subtitle="Review what's in your cart and check out" />
                        <ActionLink to="/orders" title="My Orders"
                            subtitle="View past orders and check status" />
                    </div>
                </div>
            )}

            {/* ----- Reports tile for GSRs / Area officers ---------------------- */}
            {(user?.isGsr || user?.isAreaAdmin) && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-ghasc-navy mb-3">Reports</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <ActionLink to="/reports" title="Submit a Report"
                            subtitle={
                                user?.isGsr && user?.isAreaAdmin
                                    ? 'GSR and area officer monthly reports'
                                    : user?.isGsr
                                        ? 'Monthly GSR report with celebrations'
                                        : 'Monthly area officer report'
                            } />
                    </div>
                </div>
            )}

            {/* ----- Admin tiles (sysadmin only) -------------------------------- */}
            {isSysAdmin && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-ghasc-navy mb-3">Admin</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <ActionLink to="/admin/customers" title="Customers" subtitle="Homegroups, subcommittees, etc." />
                        <ActionLink to="/admin/signups" title="Pending Signups" subtitle="Approve new signup requests" />
                        <ActionLink to="/admin/orders" title="Orders" subtitle="All homegroup orders; manage status" />
                        <ActionLink to="/admin/pending-payments" title="Pending Payments" subtitle="Process e-transfers and cash payments" />
                        <ActionLink to="/admin/fulfillment" title="Fulfillment" subtitle="Pack paid orders, print packing slips" />
                        <ActionLink to="/admin/starter-packages" title="Starter Packages" subtitle="Assemble & issue GHASC-covered kits for new groups" />
                        <ActionLink to="/admin/inventory" title="Inventory" subtitle="Catalog, prices, item images" />
                        <ActionLink to="/admin/reorder" title="Reorder" subtitle="Items below par; send reorder to regional" />
                        <ActionLink to="/admin/receiving" title="Receiving" subtitle="Record incoming shipments, auto-fill backorders" />
                        <ActionLink to="/admin/reports" title="Chair Reports" subtitle="Monthly + year-end sales & purchases" />
                        <ActionLink to="/admin/config" title="Admin Config" subtitle="Area settings, contact info, logo" />
                        <ActionLink to="/admin/import-past-orders"title="Import Past Orders"subtitle="Backfill historical orders from old PDF receipts" />
                    </div>
                </div>
            )}

            {/* ----- Your access ------------------------------------------------ */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-ghasc-navy mb-3">Your access</h3>
                {capabilities.length === 0 ? (
                    <p className="text-gray-500">
                        {isPersonal
                            ? 'Personal literature account. You can shop, place orders, and view your order history.'
                            : hasCustomer
                                ? `Standard literature rep for ${user?.customerName}. You can shop, place orders, and view your order history.`
                                : "Your account isn't linked to a customer yet — reach out to the literature chair to be linked."}
                    </p>
                ) : (
                    <ul className="space-y-1">
                        {capabilities.map((cap) => (
                            <li key={cap} className="flex items-center text-gray-700">
                                <span className="w-2 h-2 bg-ghasc-tan rounded-full mr-2" />
                                {cap}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Featured items banner
//
//  Each item is its own Link to /shop?focus=<inventoryId> so clicking takes
//  the user directly to that card on the Shop page (which scrolls to it
//  and briefly flashes a highlight ring around it). The banner header
//  "Shop now →" still links to /shop overall.
// ---------------------------------------------------------------------------
function FeaturedBanner({ items }) {
    return (
        <div className="bg-gradient-to-br from-ghasc-tan/20 to-white rounded-xl shadow-sm p-5 border border-ghasc-tan/30">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="text-ghasc-navy text-lg" aria-hidden="true">⭐</span>
                    <h3 className="text-base font-semibold text-ghasc-navy">Featured Items</h3>
                </div>
                <Link to="/shop" className="text-xs text-ghasc-navy hover:underline">
                    Shop now →
                </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map(item => (
                    <FeaturedBannerCard key={item.id} item={item} />
                ))}
            </div>
        </div>
    )
}

function FeaturedBannerCard({ item }) {
    const variantLabel = [item.year, item.color].filter(Boolean).join(' / ')

    return (
        <Link
            to={`/shop?focus=${item.id}`}
            className="flex items-center gap-3 bg-white/60 rounded-lg p-2.5 border border-white/80 hover:border-ghasc-tan hover:shadow-sm transition-all"
        >
            <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center bg-gray-50 rounded-lg">
                {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" className="max-h-12 max-w-12 object-contain" />
                ) : (
                    <span className="text-[10px] text-gray-400">no image</span>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate" title={item.description}>
                    {item.description}
                </div>
                {variantLabel && (
                    <div className="text-xs text-gray-500 truncate">{variantLabel}</div>
                )}
                <div className="text-sm font-semibold text-ghasc-navy mt-0.5">
                    ${Number(item.cost).toFixed(2)}
                </div>
            </div>
        </Link>
    )
}

function PickupDeadlineBanner({ cycle, override, overrideUntil }) {
    if (override && isOverrideActive(overrideUntil)) {
        return (
            <div className="flex items-start gap-2">
                <span aria-hidden="true">📅</span>
                <div className="whitespace-pre-line">{override}</div>
            </div>
        )
    }

    if (!cycle) return null

    const cutoffStr = formatCutoff(cycle.cutoff)
    const pickupStr = formatPickup(cycle.pickupDate)

    return (
        <div className="flex items-start gap-2">
            <span aria-hidden="true">📅</span>
            <div>
                <div>
                    All orders must be in by{' '}
                    <span className="font-medium">{cutoffStr}</span>.
                </div>
                <div className="text-xs text-blue-800 mt-0.5">
                    Pickup at the area meeting on {pickupStr}.
                </div>
            </div>
        </div>
    )
}

function isOverrideActive(overrideUntilIso) {
    if (!overrideUntilIso) return true
    const until = new Date(overrideUntilIso)
    const now = new Date()
    until.setHours(23, 59, 59, 999)
    return now <= until
}

function formatCutoff(isoString) {
    const d = new Date(isoString)
    const datePart = d.toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    })
    const timePart = d.toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true,
    })
    return `${datePart}, ${timePart}`
}

function formatPickup(isoString) {
    const d = new Date(isoString)
    return d.toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric',
    })
}

function ActionLink({ to, title, subtitle }) {
    return (
        <Link
            to={to}
            className="block p-4 border border-gray-200 rounded-lg hover:border-ghasc-navy hover:shadow-sm transition-all"
        >
            <div className="font-medium text-ghasc-navy">{title}</div>
            <div className="text-sm text-gray-500">{subtitle}</div>
        </Link>
    )
}